import { useState } from "react";
import { Share2, X } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { getProviderBySlug } from "../lib/providersManifest";

// Iter-462 — Phone layout for Side-by-Side: one stacked card per carrier,
// with a "differences only" toggle. Desktop keeps the table.
const rawValue = (spec, it) => {
  const map = {
    apr: [it.min_apr, it.max_apr],
    min_credit: it.min_credit,
    category: it.category,
    rating: it.rating,
    best_for: it.best_for,
    badge: it.badge,
    "ins-full": it.avg_annual_premium_full,
    "ins-liability": it.avg_annual_premium_liability,
    "ins-ambest": it.am_best,
    "ins-source": it.data_source,
    "ins-jdpower": getProviderBySlug(it.slug)?.trust_signals?.jd_power_score,
    "ins-naic": getProviderBySlug(it.slug)?.trust_signals?.naic_complaint_index,
  };
  return spec.key in map ? map[spec.key] : it.details?.[spec.key] ?? null;
};

export const differingSpecs = (specs, items) =>
  specs.filter((s) => new Set(items.map((it) => JSON.stringify(rawValue(s, it) ?? null))).size > 1);

export const CompareMobileCards = ({ items, specs, onRemove }) => {
  const [diffOnly, setDiffOnly] = useState(false);
  const shown = diffOnly ? differingSpecs(specs, items) : specs;
  const share = async () => {
    const url = `${window.location.origin}${window.location.pathname}?stash=${items.map((i) => i.slug).join(",")}`;
    const title = `${items.map((i) => i.name).join(" vs ")} — side-by-side on CarSavr`;
    track("compare_share", { count: items.length, method: navigator.share ? "native" : "copy" });
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — paste it into a text or email");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share right now. Try again.");
    }
  };
  return (
    <div data-testid="compare-mobile-cards" className="md:hidden px-4 pb-4">
      <button
        type="button"
        onClick={share}
        data-testid="compare-mobile-share-btn"
        className="mt-3 w-full h-11 inline-flex items-center justify-center gap-2 border border-slate-950 text-slate-950 text-sm font-bold hover:bg-slate-950 hover:text-white transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Share my comparison
      </button>
      <label className="flex items-center justify-between gap-3 py-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-700">
        Show differences only
        <input
          type="checkbox"
          checked={diffOnly}
          onChange={(e) => setDiffOnly(e.target.checked)}
          data-testid="compare-mobile-diff-toggle"
          className="w-5 h-5 accent-[#10B981]"
        />
      </label>
      {diffOnly && shown.length === 0 && (
        <p data-testid="compare-mobile-no-diff" className="text-sm text-slate-500 pb-3">
          These options match on every row we compare.
        </p>
      )}
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.slug} data-testid={`compare-mobile-card-${it.slug}`} className="border border-slate-200 bg-white">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-[#FAFAFA]">
              <span className="font-display font-black text-base text-slate-950">{it.name}</span>
              <button
                type="button"
                onClick={() => onRemove(it.slug)}
                aria-label={`Remove ${it.name} from comparison`}
                data-testid={`compare-mobile-remove-${it.slug}`}
                className="w-9 h-9 grid place-items-center text-slate-500 hover:text-slate-950"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <dl className="divide-y divide-slate-100">
              {shown.map((s) => (
                <div key={s.key} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 pt-0.5 shrink-0 max-w-[45%]">
                    {s.label}
                    {s.sub && <span className="block mt-0.5 font-medium normal-case tracking-normal text-slate-500">{s.sub}</span>}
                  </dt>
                  <dd className="text-sm text-slate-900 text-right">{s.render(it)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
};
